import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "lib/utils"

/**
 * Radix tooltip default delayDuration is 700ms which is too long for most use cases.
 * https://www.radix-ui.com/primitives/docs/components/tooltip#provider
 * Reducing it to 100ms for a snappier experience.
 */
const TooltipProvider = ({
  delayDuration = 100,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
)

const Tooltip = TooltipPrimitive.Root

const TooltipPortal = TooltipPrimitive.Portal

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipArrow = (
  props: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
) => (
  <TooltipPrimitive.Arrow
    {...props}
    className={cn("fill-muted-foreground", props.className)}
  />
)

const tooltipVariants = cva(
  "z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 border border-transparent shadow-md",
  {
    variants: {
      variant: {
        default: "bg-popover border-border text-primary",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        muted: "bg-muted-foreground text-accent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> &
  VariantProps<typeof tooltipVariants>

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, variant, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(tooltipVariants({ variant, className }))}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export {
  Tooltip,
  TooltipPortal,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipArrow,
  tooltipVariants,
}
